import { Geodata } from "@mimsights-shared/types";
import { normalizePlace } from "@/utils/cityFilter";

interface OpenStreetMapAddress {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  display_name: string;
  address: {
    road?: string;
    house_number?: string;
    postcode?: string;
    city?: string;
    town?: string;
    county?: string;
    state?: string;
    region?: string;
    country?: string;
    country_code?: string;
  };
  boundingbox: [string, string, string, string];
}

export async function getMetroArea(
  city: string,
  postal_code?: string,
  street?: string,
): Promise<string | null> {
  // A fixed language: by default Nominatim answers in the browser's, so the same county was stored
  // as "Kalmar län" or "Kalmar" ("Kalmar County") depending on who loaded the list
  let q = new URLSearchParams({ city, "accept-language": "sv" });
  if (postal_code) {
    q.append("postalcode", postal_code);
  }
  if (street) {
    q.append("street", street);
  }

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&${q.toString()}&addressdetails=1&limit=1`,
      {
        headers: {
          "User-Agent": "Mimsights/1.0",
        },
      },
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: OpenStreetMapAddress[] = await response.json();
    if (data.length === 0) {
      return null;
    }

    const address = data[0].address;
    const ret = address.county?.replace("County", "")?.trim() || null;

    if (normalizePlace(ret) === normalizePlace(city)) return null;
    return ret;
  } catch (error) {
    console.error("Error fetching metro area:", error);
    return null;
  }
}

type AddressFields = Pick<Geodata, "street" | "city" | "country" | "postcode">;
export type FoundPlace = Pick<Geodata, "street" | "city" | "country" | "postcode" | "lat" | "lng">;

export default async function findPlace<T extends AddressFields>(
  google: any,
  address: T,
): Promise<T & FoundPlace> {
  const { Place } = await google.maps.importLibrary("places");
  const { street: inputStreet, city, country, postcode } = address;
  const geodata: FoundPlace = { street: inputStreet, city, country, postcode, lat: 0, lng: 0 };
  const textQuery =
    (geodata.street ? geodata.street : "") +
    (geodata.postcode ? `, ${geodata.postcode}` : "") +
    (geodata.city ? `, ${geodata.city}` : "") +
    (geodata.country ? `, ${geodata.country}` : "");

  const request = {
    textQuery,
    fields: ["location", "addressComponents"],
    language: "en",
  };

  const { places } = await Place.searchByText(request).catch((err: unknown) => {
    console.error("places searchbytext failed", err);
    return {
      places: [],
    };
  });
  if (places.length < 1) {
    const error = new Error("no_places_found");
    throw error;
  }
  const foundPlace = places[0];

  const street = ["", ""];
  const errorMessages: string[] = [];
  foundPlace.addressComponents.forEach((component: any) => {
    if (component.types.includes("street_number")) {
      street[1] = component.longText;
    } else if (component.types.includes("route")) {
      street[0] = component.longText;
    } else if (component.types.includes("postal_code")) {
      geodata.postcode = component.longText;
    } else if (component.types.includes("postal_town")) {
      if (geodata.city && geodata.city.toLowerCase() != component.longText.toLowerCase()) {
        errorMessages.push(
          `Unable to verify City "${geodata.city}", Suggested: "${component.longText}"`,
        );
      } else {
        geodata.city = component.longText;
      }
    } else if (component.types.includes("country")) {
      if (geodata.country && geodata.country.toLowerCase() != component.longText.toLowerCase()) {
        errorMessages.push(
          `Unable to verify Country "${geodata.country}", Suggested: "${component.longText}"`,
        );
      } else {
        geodata.country = component.longText;
      }
    }
  });
  if (errorMessages.length) {
    throw new Error(errorMessages.join("\n"));
  }
  geodata.street = street.join(" ");
  const { lat, lng } = foundPlace.location;
  geodata.lat = lat();
  geodata.lng = lng();

  // Only the address fields: callers may pass form values with other geodata fields
  // (tried_grouped_as: false), which made every lookup fail
  if (Object.values(geodata).some((value) => !value)) {
    throw new Error("missing_address_data");
  }

  return { ...address, ...geodata };
}
